import React, { useState } from 'react';

function TimeTableEditor({ pageContent, setPageContent, uploading, uploadingField }) {
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  const timetableFiles = pageContent.timetableFiles || {};
  
  const allClasses = ['Nursery', 'LKG', 'UKG', ...Array(12).fill().map((_, i) => `Class ${i + 1}`), 'Class 12'];

  const handleFileUpload = async (className, section, file, isReplace = false) => {
    if (!file) return;
    
    // Validate file type - Only images allowed
    if (!file.type.startsWith('image/')) {
      alert('❌ Only image files (JPG, PNG, WEBP) are allowed for timetable upload.');
      return;
    }
    
    setUploadingFile(true);
    try {
      const { uploadImage } = await import('../../firebase/firebase-config');
      
      const fileData = await uploadImage(file, `timetable/${className}_${section}_${Date.now()}`);
      
      const newFiles = { ...timetableFiles };
      if (!newFiles[className]) newFiles[className] = {};
      newFiles[className][section] = { url: fileData, name: file.name, type: file.type };
      
      setPageContent({ ...pageContent, timetableFiles: newFiles });
      alert(`✅ Timetable for ${className} - Section ${section} ${isReplace ? 'replaced' : 'uploaded'} successfully!`);
    } catch (error) {
      alert('❌ Error uploading file: ' + error.message);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (className, section) => {
    const file = timetableFiles[className]?.[section];
    if (!file?.url) return;
    
    if (window.confirm(`Are you sure you want to delete the timetable for ${className} - Section ${section}? This action cannot be undone.`)) {
      try {
        const { deleteImageFromStorage } = await import('../../firebase/firebase-config');
        
        await deleteImageFromStorage(file.url);
        
        const newFiles = { ...timetableFiles };
        delete newFiles[className][section];
        if (Object.keys(newFiles[className]).length === 0) {
          delete newFiles[className];
        }
        
        setPageContent({ ...pageContent, timetableFiles: newFiles });
        alert(`✅ Timetable for ${className} - Section ${section} deleted successfully!`);
      } catch (error) {
        alert('❌ Error deleting file: ' + error.message);
      }
    }
  };

  const handleReplaceFile = (className, section) => {
    // Create a file input and trigger it
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,image/webp,image/jpg';
    fileInput.onchange = (e) => {
      if (e.target.files[0]) {
        handleFileUpload(className, section, e.target.files[0], true);
      }
    };
    fileInput.click();
  };

  const addNewSection = () => {
    if (!selectedClass || !selectedSection) {
      alert('Please select both class and section');
      return;
    }
    
    if (timetableFiles[selectedClass]?.[selectedSection]) {
      alert('Timetable already exists for this class and section. Use the "Replace" option to update it.');
      return;
    }
    
    // Trigger file upload
    document.getElementById('timetable-file-input').click();
  };

  const handleFileSelect = (e) => {
    if (e.target.files[0] && selectedClass && selectedSection) {
      handleFileUpload(selectedClass, selectedSection, e.target.files[0], false);
      e.target.value = '';
    } else if (!selectedClass || !selectedSection) {
      alert('Please select class and section first');
    }
  };

  return (
    <div className="page-editor">
      <div className="editor-card">
        <h3>⏰ Time Table Manager</h3>
        <p className="editor-description">Upload timetable images for each class and section. <strong>Only image files (JPG, PNG, WEBP) are supported.</strong></p>

        <div className="form-group">
          <label>Page Title</label>
          <input 
            value={pageContent.title || 'Time Table'} 
            onChange={(e) => setPageContent({...pageContent, title: e.target.value})} 
            className="form-input" 
          />
        </div>

        <div className="form-group">
          <label>Add New Timetable</label>
          <div className="upload-section">
            <div className="upload-row">
              <select 
                value={selectedClass} 
                onChange={(e) => setSelectedClass(e.target.value)}
                className="form-input"
                style={{flex: 1}}
              >
                <option value="">Select Class</option>
                {allClasses.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              
              <input 
                type="text" 
                placeholder="Section (e.g., A, B, C, Alpha, Beta)" 
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value.toUpperCase())}
                className="form-input"
                style={{flex: 1}}
              />
              
              <button 
                className="add-btn" 
                onClick={addNewSection}
                disabled={uploadingFile}
              >
                {uploadingFile ? '⏳ Uploading...' : '+ Upload Timetable Image'}
              </button>
            </div>
            
            <input 
              id="timetable-file-input"
              type="file" 
              accept="image/jpeg,image/png,image/webp,image/jpg" 
              style={{display: 'none'}} 
              onChange={handleFileSelect}
            />
          </div>
          <small style={{display: 'block', marginTop: 8, color: '#64748b'}}>
            📷 Supported formats: JPG, PNG, WEBP only. Recommended size: 1920x1080px or similar aspect ratio.
          </small>
        </div>

        <div className="form-group">
          <label>Uploaded Timetables</label>
          {Object.keys(timetableFiles).length === 0 ? (
            <div style={{textAlign: 'center', padding: 40, background: '#f8fafc', borderRadius: 12}}>
              <div style={{fontSize: 48, marginBottom: 10}}>📅</div>
              <p style={{color: '#64748b'}}>No timetables uploaded yet.</p>
              <p style={{color: '#94a3b8', fontSize: 13}}>Use the form above to add timetables for different classes and sections.</p>
            </div>
          ) : (
            <div className="timetable-list">
              {Object.keys(timetableFiles).sort().map(className => (
                <div key={className} className="class-group">
                  <h4 className="class-title">{className}</h4>
                  <div className="sections-list">
                    {Object.keys(timetableFiles[className]).sort().map(section => (
                      <div key={section} className="section-item">
                        <div className="section-info">
                          <span className="section-badge">Section {section}</span>
                          <span className="file-type">🖼️ Image</span>
                          {timetableFiles[className][section].name && (
                            <span className="file-name" title={timetableFiles[className][section].name}>
                              {timetableFiles[className][section].name.length > 30 
                                ? timetableFiles[className][section].name.substring(0, 30) + '...' 
                                : timetableFiles[className][section].name}
                            </span>
                          )}
                        </div>
                        <div className="section-actions">
                          <a 
                            href={timetableFiles[className][section].url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="view-btn"
                          >
                            👁️ View
                          </a>
                          <button 
                            className="replace-btn" 
                            onClick={() => handleReplaceFile(className, section)}
                          >
                            🔄 Replace
                          </button>
                          <button 
                            className="remove-btn" 
                            onClick={() => handleDeleteFile(className, section)}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Preview / Statistics</label>
          <div style={{background: '#f8fafc', padding: 16, borderRadius: 8, fontSize: 14}}>
            <p><strong>📚 Total Classes with Timetables:</strong> {Object.keys(timetableFiles).length}</p>
            <p><strong>📋 Total Sections:</strong> {Object.values(timetableFiles).reduce((sum, sections) => sum + Object.keys(sections).length, 0)}</p>
            <p><strong>🖼️ File Type:</strong> Images Only (JPG, PNG, WEBP)</p>
          </div>
        </div>

        <div className="info-box">
          <div className="info-icon">💡</div>
          <div className="info-content">
            <strong>Tips:</strong>
            <ul style={{margin: '8px 0 0 20px', color: '#64748b', fontSize: 13}}>
              <li>Upload clear, high-quality images of the timetable</li>
              <li>Use the "Replace" button to update an existing timetable</li>
              <li>Use "Delete" to remove a timetable completely</li>
              <li>Timetables will be displayed as images on the frontend</li>
            </ul>
          </div>
        </div>
      </div>

      <style>{`
        .editor-description {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .upload-section {
          background: #f8fafc;
          padding: 20px;
          border-radius: 12px;
          border: 1px dashed #cbd5e1;
        }

        .upload-row {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }

        .timetable-list {
          max-height: 500px;
          overflow-y: auto;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: white;
        }

        .class-group {
          border-bottom: 1px solid #e2e8f0;
        }

        .class-group:last-child {
          border-bottom: none;
        }

        .class-title {
          background: #f1f5f9;
          padding: 12px 16px;
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: #1a3a6b;
        }

        .sections-list {
          padding: 8px;
        }

        .section-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          margin: 8px;
          background: #f8fafc;
          border-radius: 8px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .section-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .section-badge {
          background: #1a3a6b;
          color: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .file-type {
          font-size: 12px;
          color: #64748b;
        }

        .file-name {
          font-size: 12px;
          color: #94a3b8;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .section-actions {
          display: flex;
          gap: 8px;
        }

        .view-btn {
          background: #10b981;
          color: white;
          padding: 6px 14px;
          border-radius: 6px;
          text-decoration: none;
          font-size: 12px;
          transition: all 0.3s;
          cursor: pointer;
        }

        .view-btn:hover {
          background: #059669;
        }

        .replace-btn {
          background: #f59e0b;
          color: white;
          padding: 6px 14px;
          border-radius: 6px;
          border: none;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .replace-btn:hover {
          background: #d97706;
        }

        .remove-btn {
          background: #ef4444;
          color: white;
          padding: 6px 14px;
          border-radius: 6px;
          border: none;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .remove-btn:hover {
          background: #dc2626;
        }

        .info-box {
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin-top: 20px;
          display: flex;
          gap: 12px;
        }

        .info-icon {
          font-size: 20px;
        }

        .info-content {
          flex: 1;
        }

        .info-content strong {
          color: #1e3a8a;
        }

        @media (max-width: 768px) {
          .upload-row {
            flex-direction: column;
          }
          
          .upload-row select,
          .upload-row input,
          .upload-row button {
            width: 100%;
          }
          
          .section-item {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .section-actions {
            width: 100%;
          }
          
          .view-btn,
          .replace-btn,
          .section-actions .remove-btn {
            flex: 1;
            text-align: center;
          }
          
          .file-name {
            max-width: 150px;
          }
        }
      `}</style>
    </div>
  );
}

export default TimeTableEditor;